import React, { Suspense, useEffect } from 'react';
import Navbar from './components/layout/Navbar';
import Home from './components/pages/Home';
import DeferredRender from './components/DeferredRender';
import {
    matchRoute,
    RouterProvider,
    useLocation,
    useNavigate,
    stripLang,
    getLangFromPath,
    SUPPORTED_LANGS,
    DEFAULT_LANG,
} from './router';
import './App.css';

import { useTranslation } from 'react-i18next';
import config from './config';
import './i18n';

const Footer = React.lazy(() => import('./components/layout/Footer'));
const StickyBookingBar = React.lazy(() => import('./components/StickyBookingBar'));
const About = React.lazy(() => import('./components/pages/About'));
const Gallery = React.lazy(() => import('./components/pages/Gallery'));
const Pricing = React.lazy(() => import('./components/pages/Pricing'));
const Contact = React.lazy(() => import('./components/pages/Contact'));
const Amenities = React.lazy(() => import('./components/pages/Amenities'));
const Apartments = React.lazy(() => import('./components/pages/Apartments'));
const Apartment = React.lazy(() => import('./components/pages/Apartment'));
const LocalGuide = React.lazy(() => import('./components/pages/LocalGuide'));
const Beaches = React.lazy(() => import('./components/pages/localGuide/Beaches'));
const Restaurants = React.lazy(() => import('./components/pages/localGuide/Restaurants'));
const Activities = React.lazy(() => import('./components/pages/localGuide/Activities'));
const DayTrips = React.lazy(() => import('./components/pages/localGuide/DayTrips'));
const GettingHere = React.lazy(() => import('./components/pages/localGuide/GettingHere'));
const Privacy = React.lazy(() => import('./components/pages/Privacy'));
const Legal = React.lazy(() => import('./components/pages/Legal'));
const HouseRules = React.lazy(() => import('./components/pages/HouseRules'));
const NotFound = React.lazy(() => import('./components/pages/NotFound'));

const apartmentBySlug = Object.values(config.apartments).reduce((apartments, apartment) => ({
    ...apartments,
    [apartment.slug]: apartment,
}), {});

const routes = [
    { path: '/', element: <Home /> },
    { path: '/about', element: <About /> },
    { path: '/gallery', element: <Gallery /> },
    { path: '/pricing', element: <Pricing /> },
    { path: '/amenities', element: <Amenities /> },
    { path: '/apartments', element: <Apartments /> },
    {
        path: '/apartments/:slug',
        render: ({ params }) => {
            const apartment = apartmentBySlug[params.slug];
            return apartment ? <Apartment apartmentKey={apartment.id} /> : <NotFound />;
        },
    },
    { path: '/local-guide', element: <LocalGuide /> },
    { path: '/local-guide/beaches', element: <Beaches /> },
    { path: '/local-guide/restaurants', element: <Restaurants /> },
    { path: '/local-guide/activities', element: <Activities /> },
    { path: '/local-guide/day-trips', element: <DayTrips /> },
    { path: '/local-guide/getting-here', element: <GettingHere /> },
    { path: '/contact', element: <Contact /> },
    { path: '/privacy', element: <Privacy /> },
    { path: '/legal', element: <Legal /> },
    { path: '/house-rules', element: <HouseRules /> },
];

const Loading = ({ label }) => (
    <div className="loading">
        <p>{label}</p>
    </div>
);

const RouteOutlet = () => {
    const location = useLocation();
    // Routes are defined against logical paths ('/about'); the URL carries a
    // language prefix ('/en/about'), so strip it before matching.
    const matchedRoute = matchRoute(routes, stripLang(location.pathname));

    if (!matchedRoute) {
        return <NotFound />;
    }

    const { route, match } = matchedRoute;
    return route.render ? route.render(match) : route.element;
};

// Keeps the URL and the active language in lock-step:
//  - a bare URL ('/about') is redirected to its language-prefixed form using the
//    visitor's detected language, preserving query/hash;
//  - on every navigation, i18n follows the language in the URL.
const LanguageSync = () => {
    const location = useLocation();
    const navigate = useNavigate();
    // No namespace + no suspense: this component only needs the i18n instance to
    // read/sync the active language, never a translated string, so it must never
    // suspend (it renders outside a Suspense boundary).
    const { i18n } = useTranslation(undefined, { useSuspense: false });

    useEffect(() => {
        const lang = getLangFromPath(location.pathname);

        if (!lang) {
            const detected = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;
            const base = location.pathname === '/' ? '' : location.pathname;
            navigate(`/${detected}${base}${location.search}${location.hash}`, { replace: true });
            return;
        }

        if (i18n.language !== lang) {
            i18n.changeLanguage(lang);
        }
    }, [location.pathname, location.search, location.hash, navigate, i18n]);

    return null;
};

const AppLayout = () => {
    const { t } = useTranslation('common');

    return (
        <Suspense fallback={<Loading label={t('loading')} />}>
            <div className="app">
                <main>
                    <Navbar />
                    <RouteOutlet />
                </main>

                <DeferredRender rootMargin="1200px" idleTimeout={2200}>
                    <Suspense fallback={null}>
                        <Footer />
                    </Suspense>
                </DeferredRender>
                <DeferredRender idleTimeout={1800}>
                    <Suspense fallback={null}>
                        <StickyBookingBar />
                    </Suspense>
                </DeferredRender>
            </div>
        </Suspense>
    );
};

function App() {
    return (
        <RouterProvider>
            <LanguageSync />
            <Suspense fallback={null}>
                <AppLayout />
            </Suspense>
        </RouterProvider>
    );
}

export default App;
